import { useEffect } from "react";
import { createPortal } from "react-dom";

/**
 * #46.2: Fælles modal-wrapper der rendres via en PORTAL til document.body.
 *
 * Hvorfor portal: en CSS `transform`/`filter`/`animation` på en forfader gør
 * elementet til "containing block" for position:fixed-børn — så en modal ankrer
 * til wrapperen i stedet for viewporten og lander langt nede på siden (brugeren
 * skal scrolle for at finde den). Ved at portale til <body> escaper vi ENHVER
 * transformeret forfader, så `fixed inset-0` altid ankrer til den ægte viewport.
 *
 * Layout:
 *  - Centreret i viewporten på BÅDE mobil og desktop (items-center).
 *  - Margin i alle sider (p-4) så modalen ikke rører kanten på mobil.
 *  - Indholdet får max-h + intern scroll; baggrunds-scroll låses (body + html
 *    + overscroll-contain) så indhold bag ikke kan scrolle med.
 *
 * Props:
 *  - onClose: kaldes ved klik på baggrunden og ved Escape (valgfri).
 *  - children: selve dialog-indholdet (typisk en kort-boks med max-w-*).
 *  - className: ekstra klasser til baggrunds-overlay (valgfri).
 */
export default function ModalPortal({ onClose, children, className = "" }) {
  // Luk på Escape + lås baggrunds-scroll (body OG html) mens modalen er åben.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && onClose) onClose(); };
    document.addEventListener("keydown", onKey);
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, [onClose]);

  return createPortal(
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 ${className}`}
      onClick={onClose}
    >
      <div
        className="w-full max-h-[88vh] overflow-y-auto overscroll-contain flex justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}
